// SPDX-FileCopyrightText: Copyright 2025 Dash0 Inc.

"use client";

import Image from "next/image";
import { useState } from "react";

interface VideoProps {
	/** YouTube video ID (e.g., "cScM9n2n4ck") */
	id: string;
	/** Video title for accessibility */
	title: string;
	/** Optional video length display (e.g., "5:30") */
	length?: string;
	/** Custom CSS classes */
	className?: string;
}

export function Video({ id, title, length, className = "" }: VideoProps) {
	const [showVideo, setShowVideo] = useState(false);

	const thumbnailUrl = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
	const embedUrl = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`;

	if (showVideo) {
		return (
			<div className={`aspect-video w-full overflow-hidden rounded-lg ${className}`}>
				<iframe
					src={embedUrl}
					title={title}
					allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
					allowFullScreen
					className="h-full w-full"
				/>
			</div>
		);
	}

	return (
		<div className={`group relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg ${className}`}>
			<Image
				src={thumbnailUrl}
				alt={`${title} - Click to play video`}
				fill
				className="object-cover transition-transform group-hover:scale-105"
				sizes="100vw"
			/>
			
			{/* Play button overlay */}
			<div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity group-hover:bg-black/30">
				<button
					onClick={() => setShowVideo(true)}
					className="flex items-center gap-3 rounded-lg bg-black/60 px-4 py-3 text-white backdrop-blur-sm transition-all hover:bg-black/80"
					aria-label={`Play video: ${title}`}
				>
					<svg
						className="h-6 w-6 fill-current"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path d="M8 5v14l11-7z" />
					</svg>
					<div className="flex flex-col text-left">
						<span className="text-sm font-medium">Play video</span>
						{length && <span className="text-xs opacity-75">{length}</span>}
					</div>
				</button>
			</div>
		</div>
	);
}